import { useEffect, useState } from 'react'
import { ImageIcon, Loader2, Send, Users } from 'lucide-react'

import { PromotionalCustomerPicker } from '@/components/offers/PromotionalCustomerPicker'
import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { Button } from '@/components/ui/button'
import { fetchMessagingSettings, sendPromotionalMessage } from '@/lib/api/messagingSettings'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-base',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

/** @typedef {'pick' | 'marketing_consent' | 'all_with_phone'} SendMode */

/**
 * @param {{ step: number, title: string, children: import('react').ReactNode }} props
 */
function Step({ step, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
          {step}
        </span>
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="pl-11">{children}</div>
    </section>
  )
}

/**
 * @param {{
 *   mode: SendMode,
 *   current: SendMode,
 *   title: string,
 *   description: string,
 *   onSelect: () => void,
 * }} props
 */
function SendModeCard({ mode, current, title, description, onSelect }) {
  const selected = current === mode

  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-4 transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'border-border/70 bg-card hover:bg-muted/30',
      )}
    >
      <input
        type="radio"
        name="send-mode"
        className="mt-1"
        checked={selected}
        onChange={onSelect}
      />
      <span>
        <span className="block font-medium text-foreground">{title}</span>
        <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
      </span>
    </label>
  )
}

export function SendOffersPage() {
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sendResult, setSendResult] = useState('')
  const [offerText, setOfferText] = useState('')
  const [statusLabel, setStatusLabel] = useState('')
  const [consentCount, setConsentCount] = useState(0)
  const [allCount, setAllCount] = useState(0)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  /** @type {[SendMode, Function]} */
  const [sendMode, setSendMode] = useState('pick')

  useEffect(() => {
    let active = true
    setLoading(true)

    fetchMessagingSettings()
      .then((payload) => {
        if (!active) return
        setStatusLabel(payload.whatsapp?.status_label ?? '')
        setConsentCount(payload.promotional_recipient_counts?.marketing_consent ?? 0)
        setAllCount(payload.promotional_recipient_counts?.all_with_phone ?? 0)
      })
      .catch((err) => {
        if (active) {
          setError(apiErrorMessage(err, 'Could not load messaging settings.'))
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const recipientCount = sendMode === 'pick'
    ? selectedIds.size
    : sendMode === 'marketing_consent'
      ? consentCount
      : allCount

  const canSend = offerText.trim().length > 0 && recipientCount > 0 && !sending

  async function handleSend() {
    if (!canSend) {
      if (!offerText.trim()) {
        setError('Write your offer message first.')
      } else if (sendMode === 'pick' && selectedIds.size === 0) {
        setError('Tick at least one customer from the list.')
      }
      return
    }

    setSending(true)
    setError('')
    setSendResult('')

    try {
      const result = sendMode === 'pick'
        ? await sendPromotionalMessage({
            offer_text: offerText,
            customer_ids: Array.from(selectedIds),
          })
        : await sendPromotionalMessage({
            offer_text: offerText,
            audience: sendMode,
          })

      setSendResult(
        `Done — sent to ${result.sent} customer${result.sent === 1 ? '' : 's'}.`
        + (result.failed > 0 ? ` ${result.failed} could not be delivered.` : ''),
      )
      setOfferText('')
      setSelectedIds(new Set())
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send messages.'))
    } finally {
      setSending(false)
    }
  }

  return (
    <SalonPageShell
      title="Send offers"
      description="Three steps: write your deal, choose who gets it, then send."
      className="max-w-none pb-28"
    >
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="salon-panel space-y-8 p-4 sm:p-6">
          {statusLabel ? (
            <p className="text-sm text-muted-foreground">{statusLabel}</p>
          ) : null}

          <Step step={1} title="Write your offer">
            <textarea
              id="offer-text"
              rows={4}
              value={offerText}
              onChange={(event) => setOfferText(event.target.value)}
              className={cn(inputClass, 'min-h-[6rem] resize-y')}
              placeholder="Example: 20% off keratin this week — call us to book!"
            />
            <div className="mt-4 flex gap-3 rounded-xl border border-dashed border-border/70 bg-muted/20 p-4">
              <ImageIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
              <div className="text-sm">
                <p className="font-medium text-foreground">Photos with your offer</p>
                <p className="mt-1 text-muted-foreground">
                  Text offers work today. To send a picture too, WhatsApp needs an approved image
                  template from Meta — we can add that when you go live. For now, describe the deal
                  in words or paste a link in your message.
                </p>
              </div>
            </div>
          </Step>

          <Step step={2} title="Who should get it?">
            <div className="grid gap-3 lg:grid-cols-3">
              <SendModeCard
                mode="pick"
                current={sendMode}
                title="I choose from the list"
                description="Best when you want to check visits and spend first."
                onSelect={() => setSendMode('pick')}
              />
              <SendModeCard
                mode="marketing_consent"
                current={sendMode}
                title="Everyone who agreed to offers"
                description={`${consentCount} customer${consentCount === 1 ? '' : 's'} — no list needed.`}
                onSelect={() => setSendMode('marketing_consent')}
              />
              <SendModeCard
                mode="all_with_phone"
                current={sendMode}
                title="Everyone with a phone number"
                description={`${allCount} customer${allCount === 1 ? '' : 's'} — sends to the whole list.`}
                onSelect={() => setSendMode('all_with_phone')}
              />
            </div>
          </Step>

          {sendMode === 'pick' ? (
            <Step step={3} title="Tick the customers to message">
              <PromotionalCustomerPicker
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            </Step>
          ) : (
            <section className="flex items-start gap-3 rounded-xl bg-muted/25 px-4 py-4 pl-14 text-sm text-muted-foreground">
              <Users className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <p>
                You chose a ready-made group — no need to tick names.
                {' '}
                <strong className="font-medium text-foreground">
                  {recipientCount}
                </strong>
                {' '}
                {recipientCount === 1 ? 'person' : 'people'}
                {' '}
                will get your WhatsApp when you tap Send below.
              </p>
            </section>
          )}
        </div>
      )}

      {!loading ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 px-4 py-4 backdrop-blur-md sm:px-6">
          <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 text-sm">
              {sendResult ? (
                <p className="font-medium text-primary" role="status">{sendResult}</p>
              ) : error ? (
                <p className="text-destructive" role="alert">{error}</p>
              ) : (
                <p className="text-muted-foreground">
                  Ready to send to
                  {' '}
                  <span className="font-semibold text-foreground">{recipientCount}</span>
                  {' '}
                  {recipientCount === 1 ? 'customer' : 'customers'}
                </p>
              )}
            </div>
            <Button
              type="button"
              size="lg"
              disabled={!canSend}
              onClick={handleSend}
              className="h-12 w-full shrink-0 rounded-xl text-base sm:w-auto sm:min-w-[14rem]"
            >
              <Send className="mr-2 h-5 w-5" />
              {sending ? 'Sending…' : 'Send WhatsApp'}
            </Button>
          </div>
        </div>
      ) : null}
    </SalonPageShell>
  )
}
